import { computed, onBeforeUnmount, ref, watch, type WatchSource } from 'vue'

/**
 * Global editing registry: version-triggered auto-reloads must never interrupt
 * a user who is in the middle of editing something. Components register their
 * editing state here (open dialogs, dirty editors); a focused text-entry
 * element counts as editing too, so plain inline inputs are covered without
 * per-component wiring.
 */

const editingCount = ref(0)
const textEntryFocused = ref(false)

const NON_TEXT_INPUT_TYPES = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image', 'range', 'hidden'])

function isTextEntryElement(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false
  if (element.isContentEditable) return true
  if (element instanceof HTMLTextAreaElement) return true
  if (element instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(element.type)

  return false
}

function syncFocusState() {
  textEntryFocused.value = isTextEntryElement(document.activeElement)
}

let focusListenersInstalled = false

function installFocusListeners() {
  if (focusListenersInstalled || typeof document === 'undefined') return

  focusListenersInstalled = true
  document.addEventListener('focusin', syncFocusState)
  // `document.activeElement` still points at the old element while `focusout`
  // is dispatched – defer one tick so we read the settled focus target.
  document.addEventListener('focusout', () => {
    window.setTimeout(syncFocusState, 0)
  })
  syncFocusState()
}

export const isEditing = computed(() => editingCount.value > 0 || textEntryFocused.value)

/**
 * Mark an editing session as active. Returns a release function that is safe
 * to call multiple times.
 */
export function beginEditing(): () => void {
  let released = false
  editingCount.value += 1

  return () => {
    if (released) return
    released = true
    editingCount.value -= 1
  }
}

/**
 * Component-scoped registration: while `source` is truthy the app counts as
 * "editing" and pending version reloads are deferred. Automatically released
 * when the component unmounts.
 */
export function useEditingGuard(source: WatchSource<boolean>) {
  let release: (() => void) | null = null

  watch(source, (active) => {
    if (active && !release) {
      release = beginEditing()
    } else if (!active && release) {
      release()
      release = null
    }
  }, { immediate: true })

  onBeforeUnmount(() => {
    release?.()
    release = null
  })
}

export function useEditingState() {
  installFocusListeners()

  return { isEditing, beginEditing }
}
